import React, { useEffect, useRef } from "react";
import HubTwoTone from "@mui/icons-material/HubTwoTone";
import "./landing.css";

/* ============================================================
   Hook: useParticles
   ============================================================ */
function useParticles(canvasRef: React.RefObject<HTMLCanvasElement>) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const PARTICLE_COUNT = 38;
    const CONNECTION_RADIUS = 100;
    const REPULSION_RADIUS = 120;
    const DAMPING = 0.998;
    const GOLD = "201, 168, 76";

    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      alpha: number;
    }

    let particles: Particle[] = [];
    let mouse = { x: -9999, y: -9999 };
    let animFrameId: number;
    let width = 0;
    let height = 0;

    function resize() {
      const parent = canvas!.parentElement;
      width = parent ? parent.clientWidth : window.innerWidth;
      height = parent ? parent.clientHeight : window.innerHeight;
      canvas!.width = width;
      canvas!.height = height;
    }

    function initParticles() {
      particles = Array.from({ length: PARTICLE_COUNT }, () => {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 0.3 + 0.15;
        return {
          x: Math.random() * width,
          y: Math.random() * height,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          radius: Math.random() * 2 + 0.8,
          alpha: Math.random() * 0.5 + 0.15,
        };
      });
    }

    function draw() {
      ctx!.clearRect(0, 0, width, height);

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i];
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_RADIUS) {
            const lineAlpha = (1 - dist / CONNECTION_RADIUS) * 0.35;
            ctx!.beginPath();
            ctx!.moveTo(a.x, a.y);
            ctx!.lineTo(b.x, b.y);
            ctx!.strokeStyle = `rgba(${GOLD}, ${lineAlpha})`;
            ctx!.lineWidth = 0.5;
            ctx!.stroke();
          }
        }
      }

      // Update and draw particles
      for (const p of particles) {
        // Mouse repulsion
        const mdx = p.x - mouse.x;
        const mdy = p.y - mouse.y;
        const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
        if (mdist < REPULSION_RADIUS && mdist > 0) {
          const force = (REPULSION_RADIUS - mdist) / REPULSION_RADIUS;
          p.vx += (mdx / mdist) * force * 0.6;
          p.vy += (mdy / mdist) * force * 0.6;
        }

        // Velocity damping
        p.vx *= DAMPING;
        p.vy *= DAMPING;

        // Update position
        p.x += p.vx;
        p.y += p.vy;

        // Wrap around edges
        if (p.x < 0) p.x = width;
        if (p.x > width) p.x = 0;
        if (p.y < 0) p.y = height;
        if (p.y > height) p.y = 0;

        // Draw particle
        ctx!.beginPath();
        ctx!.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx!.fillStyle = `rgba(${GOLD}, ${p.alpha})`;
        ctx!.fill();
      }

      animFrameId = requestAnimationFrame(draw);
    }

    function onMouseMove(e: MouseEvent) {
      const rect = canvas!.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    }

    function onMouseLeave() {
      mouse.x = -9999;
      mouse.y = -9999;
    }

    function onResize() {
      resize();
    }

    resize();
    initParticles();
    draw();

    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseleave", onMouseLeave);
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(animFrameId);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("resize", onResize);
    };
  }, [canvasRef]);
}

/* ============================================================
   Hook: useScrollReveal
   ============================================================ */
function useScrollReveal() {
  const elementsRef = useRef<Set<HTMLElement>>(new Set());

  const registerReveal: React.RefCallback<HTMLElement> = React.useCallback((element) => {
    if (element) elementsRef.current.add(element);
  }, []);

  useEffect(() => {
    const elements = Array.from(elementsRef.current);

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        }
      },
      { threshold: 0.15, rootMargin: "-20% 0px -20% 0px" },
    );

    elements.forEach((element) => observer.observe(element));

    return () => {
      observer.disconnect();
    };
  }, []);

  return registerReveal;
}

/* ============================================================
   Hook: useNavScroll
   ============================================================ */
function useNavScroll(navRef: React.RefObject<HTMLElement>) {
  useEffect(() => {
    function onScroll() {
      const nav = navRef.current;
      if (!nav) return;
      if (window.scrollY > 50) {
        nav.classList.add("scrolled");
      } else {
        nav.classList.remove("scrolled");
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [navRef]);
}

/* ============================================================
   Component: TvWaveCanvas — animated sine wave via canvas
   ============================================================ */
function TvWaveCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = 160;
    canvas.height = 80;
    let offset = 0;
    let raf: number;

    function draw() {
      if (!ctx) return;
      ctx.clearRect(0, 0, 160, 80);

      // Draw 2 layered waves for depth
      drawWave(ctx, offset, 18, 0.7, 2);
      drawWave(ctx, offset * 0.7, 12, 0.3, 1.5);

      offset += 0.04;
      raf = requestAnimationFrame(draw);
    }

    function drawWave(
      c: CanvasRenderingContext2D,
      phase: number,
      amplitude: number,
      alpha: number,
      lineWidth: number,
    ) {
      c.beginPath();
      c.strokeStyle = `rgba(59, 130, 246, ${alpha})`;
      c.lineWidth = lineWidth;
      c.lineCap = "round";

      for (let x = 0; x <= 160; x++) {
        const y = 40 + Math.sin((x / 160) * Math.PI * 4 + phase) * amplitude;
        if (x === 0) c.moveTo(x, y);
        else c.lineTo(x, y);
      }
      c.stroke();
    }

    draw();
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="landing-tv-wave">
      <canvas ref={ref} width={160} height={80} />
    </div>
  );
}

/* ============================================================
   Main Component: LandingPage
   ============================================================ */
export const LandingPage: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const navRef = useRef<HTMLElement>(null);

  useParticles(canvasRef);
  const registerReveal = useScrollReveal();
  useNavScroll(navRef);

  const logoSrc = `${window.location.origin}/assets/Logo_DAKASA_V1_Branco.png`;

  return (
    <div id="landingPage" className="landing-page">
      {/* Background layer */}
      <div id="landingBg" className="landing-background" />

      {/* Content layer */}
      <div id="landingContent" className="landing-content">
        {/* Nav */}
        <nav id="landingNav" className="landing-nav" ref={navRef}>
          <div id="landingNavLogo" className="landing-nav-logo">
            <img src={logoSrc} alt="DaKasa" />
          </div>
          <div id="landingNavActions" className="landing-nav-actions">
            <a id="landingNavLogin" className="landing-nav-login" href="/auth">
              Entrar
            </a>
          </div>
        </nav>

        {/* Hero */}
        <section id="landingHero" className="landing-hero">
          <canvas ref={canvasRef} id="landingHeroCanvas" className="landing-hero-canvas" />
          <div id="landingHeroContent" className="landing-hero-content">
            <h1>
              Sua marca no
              <br />
              <em>centro da atenção</em>
            </h1>
            <p>Anuncie na rede social com milhares de usuários engajados.</p>
            <a href="/solicitar-acesso" className="landing-cta">
              Solicitar Acesso →
            </a>
          </div>
        </section>

        {/* Breathe 1 */}
        <div className="landing-breathe">
          <div className="landing-breathe-orb" />
        </div>

        {/* "O que é" section */}
        <section
          id="landingWhatIs"
          className="landing-overview landing-reveal"
          ref={registerReveal}
        >
          <div id="landingWhatIsText" className="landing-overview-text">
            <span className="landing-kicker">A Rede Social</span>
            <h2>
              Onde <em>audiência real</em> encontra a sua marca
            </h2>
            <p>
              DaKasa é uma rede social que possibilita aos usuários interagirem em espaços
              comunitários escutando rádio, assistindo TV e batendo papo. Sua empresa anuncia
              diretamente para essa audiência engajada, sem contar com as negociações diárias por
              espaços publicitários de visualização premium.
            </p>
          </div>

          <div id="landingOrbit" className="landing-orbit">
            <div className="landing-orbit-ring">
              <div className="landing-orbit-dot" />
            </div>
            <div className="landing-orbit-ring">
              <div className="landing-orbit-dot" />
            </div>
            <div className="landing-orbit-ring">
              <div className="landing-orbit-dot" />
            </div>
            <div className="landing-orbit-center">
              <HubTwoTone sx={{ fontSize: 20, color: "#c9a84c" }} />
            </div>
          </div>
        </section>

        {/* Breathe 2 */}
        <div className="landing-breathe">
          <div className="landing-breathe-line" />
        </div>

        {/* "Como funciona" section */}
        <section id="landingHowWorks" className="landing-how landing-reveal" ref={registerReveal}>
          <div className="how-header">
            <h2>Comece a anunciar em 3 passos</h2>
            <p>Simples, rápido e com resultados reais</p>
          </div>

          <div className="landing-timeline">
            <div className="landing-step">
              <div className="landing-step-diamond" />
              <h4>Crie seus anúncios</h4>
              <p>
                Suba seus áudios, vídeos ou banners direto no painel. Configure orçamento e onde
                quer aparecer.
              </p>
            </div>

            <div className="landing-step">
              <div className="landing-step-diamond" />
              <h4>Negocie os melhores espaços</h4>
              <p>
                Participe da negociação diária e dispute os espaços mais visíveis das comunidades.
              </p>
            </div>

            <div className="landing-step">
              <div className="landing-step-diamond" />
              <h4>Apareça para milhares</h4>
              <p>
                Seu conteúdo roda nas comunidades ao vivo. Acompanhe os resultados em tempo real
                pelo painel.
              </p>
            </div>
          </div>
        </section>

        {/* Breathe 3 */}
        <div className="landing-breathe">
          <div className="landing-breathe-ripple">
            <span />
            <span />
            <span />
          </div>
        </div>

        {/* Lanes section */}
        <div id="landingLanes" className="landing-lanes">
          {/* Lanes header */}
          <div className="landing-lanes-header landing-reveal" ref={registerReveal}>
            <h2>Três canais para sua marca brilhar</h2>
            <p>Escolha onde e como seu público encontra você</p>
          </div>

          <div className="landing-lanes-list">
            {/* Lane Rádio */}
            <div
              className="landing-lane landing-lane-radio landing-reveal"
              data-type="radio"
              ref={registerReveal}
            >
              <div className="landing-lane-visual">
                <div className="landing-lane-glow" />
                <div className="landing-radio-bars">
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              </div>
              <div className="landing-lane-info">
                <span className="landing-lane-tag">Rádio</span>
                <h4>Sua marca no ouvido de quem está ao vivo</h4>
                <p>
                  Spots de áudio que rodam nas transmissões das comunidades com rádio. Audiência
                  cativa, sem skip. Sua mensagem chega inteira.
                </p>
              </div>
            </div>

            {/* Lane TV */}
            <div
              className="landing-lane landing-lane-tv landing-reveal"
              data-type="tv"
              ref={registerReveal}
            >
              <div className="landing-lane-visual">
                <div className="landing-lane-glow" />
                <TvWaveCanvas />
              </div>
              <div className="landing-lane-info">
                <span className="landing-lane-tag">TV</span>
                <h4>Vídeos que capturam a atenção</h4>
                <p>
                  Conteúdo em vídeo nos intervalos das comunidades com TV. Alto impacto visual para
                  uma audiência que já está assistindo.
                </p>
              </div>
            </div>

            {/* Lane Átrio */}
            <div
              className="landing-lane landing-lane-hall landing-reveal"
              data-type="hall"
              ref={registerReveal}
            >
              <div className="landing-lane-visual">
                <div className="landing-lane-glow" />
                <div className="landing-hall-feed">
                  {/* Card 1 — front (sponsored post) */}
                  <div className="landing-hall-card">
                    <div className="landing-hall-card-header">
                      <div className="landing-hall-card-avatar" />
                      <span className="landing-hall-card-name">Sua Marca</span>
                      <span className="landing-hall-card-badge">PATROCINADO</span>
                    </div>
                    <div className="landing-hall-card-image">
                      <span>&#9654;</span>
                    </div>
                    <div className="landing-hall-card-content">
                      <div className="landing-hall-card-line" />
                      <div className="landing-hall-card-line" />
                    </div>
                    <div className="landing-hall-card-actions">
                      <div className="landing-hall-card-action" />
                      <div className="landing-hall-card-action" />
                      <div className="landing-hall-card-action" />
                    </div>
                  </div>

                  {/* Card 2 — behind */}
                  <div className="landing-hall-card">
                    <div className="landing-hall-card-header">
                      <div
                        className="landing-hall-card-avatar"
                        style={{ background: "rgba(255,255,255,0.12)" }}
                      />
                      <span className="landing-hall-card-name" style={{ opacity: 0.5 }}>
                        Usuário
                      </span>
                    </div>
                    <div className="landing-hall-card-content">
                      <div className="landing-hall-card-line" />
                    </div>
                  </div>

                  {/* Card 3 — furthest back */}
                  <div className="landing-hall-card">
                    <div className="landing-hall-card-content">
                      <div className="landing-hall-card-line" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="landing-lane-info">
                <span className="landing-lane-tag">Átrio</span>
                <h4>Presença constante no feed principal</h4>
                <p>
                  Conteúdo patrocinado direto no Átrio, o espaço central onde todos os usuários
                  passam. Visibilidade garantida.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Breathe 4 */}
        <div className="landing-breathe" />

        {/* O outro lado — the consumer app */}
        <section
          id="landingProduct"
          className="landing-product landing-reveal"
          ref={registerReveal}
        >
          <div className="landing-product-visual" aria-hidden="true">
            <div className="landing-product-aura" />
            <div className="landing-phone">
              <div className="landing-phone-notch" />
              <div className="landing-phone-screen">
                <div className="landing-phone-top">
                  <span className="landing-phone-live">
                    <i />
                    AO VIVO
                  </span>
                  <span className="landing-phone-brand">DaKasa</span>
                </div>
                <div className="landing-phone-eq">
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
                <div className="landing-phone-card landing-phone-card-sponsored">
                  <div className="landing-phone-card-head">
                    <div className="landing-phone-avatar" />
                    <div className="landing-phone-meta">
                      <b>Sua Marca</b>
                      <span className="landing-phone-tag">PATROCINADO</span>
                    </div>
                  </div>
                  <div className="landing-phone-thumb">
                    <span>&#9654;</span>
                  </div>
                </div>
                <div className="landing-phone-card landing-phone-card-muted">
                  <div className="landing-phone-card-head">
                    <div className="landing-phone-avatar" />
                    <div className="landing-phone-line" />
                  </div>
                </div>
                <div className="landing-phone-card landing-phone-card-muted landing-phone-card-far">
                  <div className="landing-phone-card-head">
                    <div className="landing-phone-avatar" />
                    <div className="landing-phone-line landing-phone-line-short" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="landing-product-text">
            <span className="landing-kicker">O outro lado</span>
            <h2>
              Vem ver <em>por dentro</em>
            </h2>
            <p>
              Já mostramos como funciona. Agora é o convite: entra, explora e sente a DaKasa
              acontecendo ao vivo.
            </p>
            <div className="landing-product-actions">
              <a
                href="https://dakasa.me"
                target="_blank"
                rel="noopener noreferrer"
                className="landing-cta"
              >
                Conheça a DaKasa →
              </a>
            </div>
          </div>
        </section>

        {/* Breathe 5 */}
        <div className="landing-breathe">
          <div className="landing-breathe-orb" />
        </div>

        {/* Footer CTA */}
        <section
          id="landingFooterCta"
          className="landing-footer-cta landing-reveal"
          ref={registerReveal}
        >
          <h3>Pronto para anunciar?</h3>
          <p>Solicite acesso e coloque sua marca na frente de milhares</p>
          <a href="/solicitar-acesso" className="landing-cta landing-cta-shimmer">
            Solicitar Acesso →
          </a>
        </section>

        {/* Footer bottom */}
        <footer id="landingFooterBottom" className="landing-footer-bottom">
          <div id="landingFooterBottomLogo" className="landing-footer-logo">
            <img src={logoSrc} alt="DaKasa" />
          </div>
          <ul className="landing-footer-links">
            <li>
              <a href="/auth">Entrar</a>
            </li>
            <li>
              <a href="/solicitar-acesso">Solicitar Acesso</a>
            </li>
          </ul>
          <small id="landingFooterCopyright" className="landing-footer-copyright">
            © 2026 DaKasa LTDA
          </small>
        </footer>
      </div>
    </div>
  );
};
